import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mockObjects } from './objectQueries';
import { getMockRoles } from './roleQueries';
import { createPersistedMockData } from '../mockDataManager';

// ============================================
// TYPES
// ============================================

export interface FieldPermission {
  id: string;
  fieldId: string;
  fieldLabel: string;
  fieldApiName: string;
  objectName: string;
  objectLabel: string;
  roleId: string;
  roleName: string;
  canView: boolean;
  canEdit: boolean;
  createdBy: string;
  createdDate: string;
  lastModified: string;
  lastModifiedBy: string;
}

export interface FieldSecuritySummary {
  fieldId: string;
  fieldLabel: string;
  objectName: string;
  totalRoles: number;
  rolesCanView: number;
  rolesCanEdit: number;
  isPublic: boolean;
  isRestricted: boolean;
  lastModified: string;
}

// ============================================
// MOCK DATA
// ============================================

// Function to generate default field permissions
function generateDefaultFieldPermissions(): FieldPermission[] {
  const permissions: FieldPermission[] = [];
  const mockRoles = getMockRoles();

  // Generate field permissions for all custom objects
  mockObjects.filter(obj => !obj.isCustom).forEach(obj => {
  // Create mock fields for objects that don't have them
  const fields = [
    { id: `${obj.id}_f1`, label: 'Name', apiName: 'Name', type: 'text' },
    { id: `${obj.id}_f2`, label: 'Annual Revenue', apiName: 'Annual_Revenue__c', type: 'currency' },
    { id: `${obj.id}_f3`, label: 'Email', apiName: 'Email', type: 'email' },
    { id: `${obj.id}_f4`, label: 'SSN', apiName: 'SSN__c', type: 'text' },
    { id: `${obj.id}_f5`, label: 'Status', apiName: 'Status', type: 'picklist' },
  ];

  fields.forEach(field => {
    // Only add permissions for sensitive fields
    const isSensitiveField =
      field.type === 'currency' ||
      field.type === 'number' ||
      field.label.toLowerCase().includes('revenue') ||
      field.label.toLowerCase().includes('ssn') ||
      field.label.toLowerCase().includes('tax') ||
      field.label.toLowerCase().includes('salary');

    if (isSensitiveField) {
      mockRoles.forEach((role, index) => {
        const isAdmin = role.name === 'System Administrator';
        const isManager = role.name.includes('Manager');
        const isReadOnly = role.name === 'Data Analyst';

        permissions.push({
          id: `fp_${field.id}_${role.id}`,
          fieldId: field.id,
          fieldLabel: field.label,
          fieldApiName: field.apiName,
          objectName: obj.apiName,
          objectLabel: obj.label,
          roleId: role.id,
          roleName: role.name,
          canView: !isReadOnly || index < 4, // Most can view
          canEdit: isAdmin || isManager, // Only admins and managers can edit
          createdBy: 'Admin',
          createdDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
          lastModified: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
          lastModifiedBy: 'Admin'
        });
      });
    } else {
      // Non-sensitive fields: everyone can view and edit by default
      mockRoles.forEach(role => {
        permissions.push({
          id: `fp_${field.id}_${role.id}`,
          fieldId: field.id,
          fieldLabel: field.label,
          fieldApiName: field.apiName,
          objectName: obj.apiName,
          objectLabel: obj.label,
          roleId: role.id,
          roleName: role.name,
          canView: true,
          canEdit: role.name !== 'Data Analyst',
          createdBy: 'System',
          createdDate: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
          lastModified: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
          lastModifiedBy: 'System'
        });
      });
    }
  });
  });

  return permissions;
}

// Create persisted field permissions using centralized mock data manager
const persistedFieldPermissions = createPersistedMockData('crm_field_permissions', generateDefaultFieldPermissions());

// Export for backwards compatibility
export const mockFieldPermissions = persistedFieldPermissions.data;

// ============================================
// QUERY KEYS
// ============================================

export const fieldPermissionKeys = {
  all: ['field-permissions'] as const,
  lists: () => [...fieldPermissionKeys.all, 'list'] as const,
  list: (fieldId?: string, roleId?: string) =>
    [...fieldPermissionKeys.lists(), { fieldId, roleId }] as const,
  summaries: () => [...fieldPermissionKeys.all, 'summaries'] as const,
  summary: (objectName: string) => [...fieldPermissionKeys.summaries(), objectName] as const,
};

// ============================================
// HOOKS
// ============================================

/**
 * Get field permissions for a specific field
 */
export function useFieldPermissions(fieldId: string) {
  return useQuery({
    queryKey: fieldPermissionKeys.list(fieldId),
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 300));
      return persistedFieldPermissions.get().filter(fp => fp.fieldId === fieldId);
    },
    staleTime: 2 * 60 * 1000,
    enabled: !!fieldId,
  });
}

/**
 * Get all field permissions for a role
 */
export function useRoleFieldPermissions(roleId: string) {
  return useQuery({
    queryKey: fieldPermissionKeys.list(undefined, roleId),
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 300));
      return persistedFieldPermissions.get().filter(fp => fp.roleId === roleId);
    },
    staleTime: 2 * 60 * 1000,
    enabled: !!roleId,
  });
}

/**
 * Get field security summaries for an object
 */
export function useFieldSecuritySummaries(objectName: string) {
  return useQuery({
    queryKey: fieldPermissionKeys.summary(objectName),
    queryFn: async (): Promise<FieldSecuritySummary[]> => {
      await new Promise(resolve => setTimeout(resolve, 400));
      const mockRoles = getMockRoles();

      const objectPermissions = persistedFieldPermissions.get().filter(fp => fp.objectName === objectName);
      const fieldIds = Array.from(new Set(objectPermissions.map(fp => fp.fieldId)));

      return fieldIds.map(fieldId => {
        const fieldPerms = objectPermissions.filter(fp => fp.fieldId === fieldId);
        const firstPerm = fieldPerms[0];
        const totalRoles = mockRoles.length;
        const rolesCanView = fieldPerms.filter(fp => fp.canView).length;
        const rolesCanEdit = fieldPerms.filter(fp => fp.canEdit).length;

        return {
          fieldId,
          fieldLabel: firstPerm.fieldLabel,
          objectName,
          totalRoles,
          rolesCanView,
          rolesCanEdit,
          isPublic: rolesCanView === totalRoles && rolesCanEdit === totalRoles,
          isRestricted: rolesCanView < totalRoles || rolesCanEdit < totalRoles,
          lastModified: firstPerm.lastModified
        };
      });
    },
    staleTime: 2 * 60 * 1000,
    enabled: !!objectName,
  });
}

/**
 * Update field permission
 */
export function useUpdateFieldPermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      permissionId,
      updates
    }: {
      permissionId: string;
      updates: { canView?: boolean; canEdit?: boolean }
    }) => {
      await new Promise(resolve => setTimeout(resolve, 300));

      const currentPerm = persistedFieldPermissions.get().find(fp => fp.id === permissionId);
      if (!currentPerm) throw new Error('Permission not found');

      // If setting canEdit to true, must also set canView to true
      if (updates.canEdit && !currentPerm.canView && updates.canView === undefined) {
        updates.canView = true;
      }

      // If setting canView to false, must also set canEdit to false
      if (updates.canView === false && currentPerm.canEdit) {
        updates.canEdit = false;
      }

      const finalUpdates = {
        ...updates,
        lastModified: new Date().toISOString(),
        lastModifiedBy: 'Current User'
      };

      const updated = persistedFieldPermissions.update(permissionId, finalUpdates);
      if (!updated) throw new Error('Permission not found');

      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: fieldPermissionKeys.list(data.fieldId) });
      queryClient.invalidateQueries({ queryKey: fieldPermissionKeys.summary(data.objectName) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Bulk update field permissions
 */
export function useBulkUpdateFieldPermissions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      fieldId,
      updates
    }: {
      fieldId: string;
      updates: Array<{ roleId: string; canView: boolean; canEdit: boolean }>;
    }) => {
      await new Promise(resolve => setTimeout(resolve, 500));

      const updatedPermissions: FieldPermission[] = [];

      updates.forEach(update => {
        const currentPerm = persistedFieldPermissions.get().find(
          fp => fp.fieldId === fieldId && fp.roleId === update.roleId
        );

        if (currentPerm) {
          // If setting canEdit to true, must also set canView to true
          if (update.canEdit && !update.canView) {
            update.canView = true;
          }

          const finalUpdates = {
            canView: update.canView,
            canEdit: update.canEdit,
            lastModified: new Date().toISOString(),
            lastModifiedBy: 'Current User'
          };

          const updated = persistedFieldPermissions.update(currentPerm.id, finalUpdates);
          if (updated) {
            updatedPermissions.push(updated);
          }
        }
      });

      return updatedPermissions;
    },
    onSuccess: (data) => {
      if (data.length > 0) {
        queryClient.invalidateQueries({ queryKey: fieldPermissionKeys.list(data[0].fieldId) });
        queryClient.invalidateQueries({ queryKey: fieldPermissionKeys.summary(data[0].objectName) });
        queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
      }
    },
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get security level label
 */
export function getSecurityLevel(summary: FieldSecuritySummary): string {
  if (summary.isPublic) return 'Public';
  if (summary.rolesCanView === 0) return 'Private';
  if (summary.rolesCanEdit === 0) return 'Read-Only';
  if (summary.rolesCanEdit < summary.totalRoles / 2) return 'Restricted';
  return 'Standard';
}

/**
 * Get security level color
 */
export function getSecurityLevelColor(summary: FieldSecuritySummary): string {
  const level = getSecurityLevel(summary);
  const colors: Record<string, string> = {
    'Public': 'green',
    'Private': 'red',
    'Read-Only': 'yellow',
    'Restricted': 'orange',
    'Standard': 'blue'
  };
  return colors[level] || 'gray';
}

/**
 * Check if permission change is valid
 */
export function isValidPermissionChange(
  currentCanView: boolean,
  currentCanEdit: boolean,
  newCanView: boolean,
  newCanEdit: boolean
): { valid: boolean; error?: string } {
  // Can't edit without view permission
  if (newCanEdit && !newCanView) {
    return {
      valid: false,
      error: 'Edit permission requires view permission'
    };
  }

  return { valid: true };
}
